import { useTheme } from "@emotion/react";
import type { IconName } from "../../types";

type ColorName =
  | "primary"
  | "secondary"
  | "tertiary"
  | "disabled"
  | "inactive"
  | "white"
  | "currentColor";

interface IconProps {
  name: IconName;
  size?: number;
  color?: ColorName;
  className?: string;
}

const iconDefaultColor: Partial<Record<IconName, ColorName>> = {
  play: "secondary",
};

export const Icon = ({
  name,
  size = 24,
  color,
  className,
}: IconProps) => {
  const theme = useTheme();
  const resolvedColor = color ?? iconDefaultColor[name] ?? "currentColor";

  // Map color names to theme values
  const getColor = (colorInput: string): string => {
    switch (colorInput) {
      case "primary":
        return theme.colors.primary[1000];
      case "secondary":
        return theme.colors.secondary[1000];
      case "tertiary":
        return theme.colors.tertiary[1000];
      case "disabled":
        return theme.colors.neutral.greyDark[1000];
      case "inactive":
        return theme.colors.neutral.greyLight[1000];
      case "white":
        return theme.colors.neutral.white;
      default:
        return colorInput;
    }
  };

  const iconColor = getColor(resolvedColor);
  const icons: Record<IconName, React.JSX.Element> = {
    user: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
          fill={iconColor}
        />
      </svg>
    ),
    "user-alert": (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <path
          d="M12 1.33325H4C2.89333 1.33325 2 2.21992 2 3.31325V10.5866C2 11.6799 2.89333 12.5666 4 12.5666H4.50667C5.04 12.5666 5.54667 12.7733 5.92 13.1466L7.06 14.2733C7.58 14.7866 8.42667 14.7866 8.94667 14.2733L10.0867 13.1466C10.46 12.7733 10.9733 12.5666 11.5 12.5666H12C13.1067 12.5666 14 11.6799 14 10.5866V3.31325C14 2.21992 13.1067 1.33325 12 1.33325ZM8 3.83325C8.86 3.83325 9.55333 4.52659 9.55333 5.38659C9.55333 6.24659 8.86 6.93992 8 6.93992C7.14 6.93992 6.44667 6.23992 6.44667 5.38659C6.44667 4.52659 7.14 3.83325 8 3.83325ZM9.78667 10.0399H6.21333C5.67333 10.0399 5.36 9.43992 5.66 8.99325C6.11333 8.31992 6.99333 7.86659 8 7.86659C9.00667 7.86659 9.88667 8.31992 10.34 8.99325C10.64 9.43992 10.32 10.0399 9.78667 10.0399Z"
          fill={iconColor}
        />
      </svg>
    ),
    back: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z"
          stroke={iconColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    alert: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
          fill={iconColor}
        />
      </svg>
    ),
    plus: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={iconColor} strokeWidth="2" />
        <path
          d="M12 8V16M8 12H16"
          stroke={iconColor}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    minus: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={iconColor} strokeWidth="2" />
        <path
          d="M8 12H16"
          stroke={iconColor}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    "caret-down": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M7 10L12 15L17 10H7Z" fill={iconColor} />
      </svg>
    ),
    home: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z"
          fill={iconColor}
        />
      </svg>
    ),
    target: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6ZM12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16Z"
          fill={iconColor}
        />
      </svg>
    ),
    check: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
          fill={iconColor}
        />
      </svg>
    ),
    bag: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M18 6H16C16 3.79 14.21 2 12 2C9.79 2 8 3.79 8 6H6C4.9 6 4 6.9 4 8V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8C20 6.9 19.1 6 18 6ZM12 4C13.1 4 14 4.9 14 6H10C10 4.9 10.9 4 12 4ZM18 20H6V8H8V10C8 10.55 8.45 11 9 11C9.55 11 10 10.55 10 10V8H14V10C14 10.55 14.45 11 15 11C15.55 11 16 10.55 16 10V8H18V20Z"
          fill={iconColor}
        />
      </svg>
    ),
    chart: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M13 11H19.95Q19.58 8.25 17.66 6.34 15.75 4.43 13 4.05M11 19.95V4.05Q8 4.43 6 6.69 4 8.95 4 12T6 17.31Q8 19.58 11 19.95M13 19.95Q15.75 19.6 17.68 17.68 19.6 15.75 19.95 13H13M12 12M12 22Q9.93 22 8.1 21.21 6.28 20.43 4.93 19.08 3.58 17.73 2.79 15.9 2 14.08 2 12T2.79 8.1Q3.58 6.28 4.93 4.93 6.28 3.58 8.1 2.79 9.93 2 12 2T15.89 2.79Q17.7 3.58 19.06 4.94 20.43 6.3 21.21 8.11 22 9.93 22 12 22 14.05 21.21 15.88 20.43 17.7 19.08 19.06 17.73 20.43 15.9 21.21 14.08 22 12 22Z"
          fill={iconColor}
        />
      </svg>
    ),
    "checkmark-circle": (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
          fill={iconColor}
        />
      </svg>
    ),
    calendar: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M7 2C7.55 2 8 2.45 8 3V4H16V3C16 2.45 16.45 2 17 2C17.55 2 18 2.45 18 3V4H19C20.1 4 21 4.9 21 6V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V6C3 4.9 3.9 4 5 4H6V3C6 2.45 6.45 2 7 2ZM5 9V19H19V9H5ZM7 11H9V13H7V11ZM11 11H13V13H11V11ZM15 11H17V13H15V11Z"
          fill={iconColor}
        />
      </svg>
    ),
    save: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3ZM12 19C10.34 19 9 17.66 9 16C9 14.34 10.34 13 12 13C13.66 13 15 14.34 15 16C15 17.66 13.66 19 12 19ZM15 9H5V5H15V9Z"
          fill={iconColor}
        />
      </svg>
    ),
    logout: (
      <svg width={size} height={size} viewBox="0 0 18 18" fill="none">
        <path
          d="M12.6 1.5H10.65C8.25 1.5 6.75 3 6.75 5.4V8.4375H10.08L8.5275 6.885C8.415 6.7725 8.3625 6.63 8.3625 6.4875C8.3625 6.345 8.415 6.2025 8.5275 6.09C8.745 5.8725 9.105 5.8725 9.3225 6.09L11.835 8.6025C12.0525 8.82 12.0525 9.18 11.835 9.3975L9.3225 11.91C9.105 12.1275 8.745 12.1275 8.5275 11.91C8.31 11.6925 8.31 11.3325 8.5275 11.115L10.08 9.5625H6.75V12.6C6.75 15 8.25 16.5 10.65 16.5H12.5925C14.9925 16.5 16.4925 15 16.4925 12.6V5.4C16.5 3 15 1.5 12.6 1.5Z"
          fill={iconColor}
        />
        <path
          d="M2.0625 8.4375C1.755 8.4375 1.5 8.6925 1.5 9C1.5 9.3075 1.755 9.5625 2.0625 9.5625H6.75V8.4375H2.0625Z"
          fill={iconColor}
        />
      </svg>
    ),
    union: (
      <svg width={size} height={size} viewBox="0 0 15 15" fill="none">
        <path
          clipRule="evenodd"
          d="m9.28499.0693356c.67609-.1469966 1.51861-.0559344 2.14771.2321074.1533.0702.2646.208486.3004.373257.147.67611.0559 1.51866-.2321 2.14773-.0899.19632-.2888.31947-.5046.31239-.2158-.00707-.4063-.14302-.4831-.34479l-.0989-.25965c-.1765.16267-.3656.33568-.55298.50469-.313.28226-.63597.56687-.9113.79455-.13717.11344-.26956.21861-.38709.30429-.09864.0719-.24663.17528-.39764.23776-.20439.08457-.44182.12021-.68918.06287-.23846-.05528-.41505-.18052-.53517-.29466-.20205-.19196-.34003-.45154-.4133-.58939l-.00936-.01762c-.04078-.07648-.07275-.13641-.09955-.18459-.12338.08265-.27337.20466-.44237.36147-.38104.35352-.77284.80286-1.04804 1.13656-.28236.34238-.78882.39105-1.13121.10868-.34239-.28236-.39105-.78881-.10869-1.13121.29095-.35279.73579-.86628 1.19484-1.29219.22804-.21158.48149-.42222.73808-.58114.22598-.13997.59159-.32851 1.00524-.29492.47415.03848.78067.32369.95297.53949.11646.14587.2137.31466.28496.44645l.06129-.05035c.24905-.20595.55222-.47272.85921-.74957.22401-.202.44243-.40236.63888-.58398l-.32622-.22162c-.17718-.120363-.26564-.334703-.22491-.544997.04073-.210292.20281-.376109.41213-.4216174zm-7.30805 5.9013144c-.80732 0-1.455104.58932-1.633655 1.32146l-.01416.05798c-.163769.67004-.329125 1.34657-.329125 3.13511 0 1.7885.165356 2.4651.329125 3.1351l.01416.058c.178551.7322.826335 1.3215 1.633655 1.3215h10.51046c.8078 0 1.4527-.5896 1.6368-1.3158l.0094-.037c.1656-.6527.3307-1.3035.3307-3.1618 0-1.85822-.1651-2.50903-.3307-3.16174l-.0094-.037c-.1841-.7262-.829-1.31581-1.6368-1.31581zm.22509 4.52735c-.00476-.3698.29118-.67341.66099-.67815l.65205-.00838c.3698-.00476.67344.29123.67819.66103.00476.3697-.29118.6734-.66098.6782l-.65205.0083c-.36981.0048-.67345-.2912-.6782-.661zm8.73017-.67815c-.3698.00474-.6657.30835-.661.67815.0048.3698.3084.6658.6783.661l.6519-.0083c.3699-.0048.6658-.3085.6611-.6782-.0048-.3698-.3085-.66579-.6782-.66103zm-3.70006 2.21655c.99007 0 1.54698-.5569 1.54698-1.547 0-.99004-.55691-1.54695-1.54698-1.54695-.99006 0-1.54698.55691-1.54698 1.54695 0 .9901.55692 1.547 1.54698 1.547z"
          fill={iconColor}
          fillRule="evenodd"
        />
      </svg>
    ),
    whatsapp: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
          fill={iconColor}
        />
      </svg>
    ),
    telegram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"
          fill={iconColor}
        />
      </svg>
    ),
    copy: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
          fill={iconColor}
        />
      </svg>
    ),
    menu: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M3 5H21V7H3V5Z" fill={iconColor} />
        <path d="M3 9H21V11H3V9Z" fill={iconColor} />
        <path d="M3 13H21V15H3V13Z" fill={iconColor} />
        <path d="M3 17H21V19H3V17Z" fill={iconColor} />
      </svg>
    ),
    close: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM15.36 14.3C15.65 14.59 15.65 15.07 15.36 15.36C15.21 15.51 15.02 15.58 14.83 15.58C14.64 15.58 14.45 15.51 14.3 15.36L12 13.06L9.7 15.36C9.55 15.51 9.36 15.58 9.17 15.58C8.98 15.58 8.79 15.51 8.64 15.36C8.35 15.07 8.35 14.59 8.64 14.3L10.94 12L8.64 9.7C8.35 9.41 8.35 8.93 8.64 8.64C8.93 8.35 9.41 8.35 9.7 8.64L12 10.94L14.3 8.64C14.59 8.35 15.07 8.35 15.36 8.64C15.65 8.93 15.65 9.41 15.36 9.7L13.06 12L15.36 14.3Z"
          fill={iconColor}
        />
      </svg>
    ),
    play: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <rect width="16" height="16" rx="4" fill={iconColor} />
        <path
          opacity="0.5"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6 5.71509L6 10.285C6 10.6799 6.3147 11 6.7029 11C6.83464 11 6.96373 10.9623 7.07544 10.8913L10.6695 8.60636C10.9987 8.39708 11.0988 7.95596 10.8931 7.6211C10.8365 7.52903 10.7601 7.45127 10.6695 7.39373L7.07544 5.10878C6.74625 4.89949 6.31259 5.00129 6.10684 5.33615C6.03702 5.44978 6 5.58109 6 5.71509Z"
          fill={theme.colors.neutral.white}
        />
      </svg>
    ),
  };

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icons[name]}
    </span>
  );
};
